import React, { useId, useMemo } from "react";
import {
  Layers,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  AlertCircle,
  Hash,
} from "lucide-react";
import {
  normalizeSectionConfig,
  validateSectionConfig,
} from "../../../components/Safety/shared/safetySectionUtils";

/**
 * Generate a collision-free section key
 */
function generateSectionKey() {
  return `sec_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
}

/**
 * SafetySectionManager
 *
 * Authoritative Section Configuration Component used in Setup.
 * Enforces contiguous question assignment naturally via section blocks.
 */
function SafetySectionManager({
  questions = [],
  sectionConfig,
  onChange,
  className = "",
}) {
  const normConfig = useMemo(
    () => normalizeSectionConfig(sectionConfig),
    [sectionConfig]
  );

  const totalQuestions = questions.length;
  const radioInstanceId = useId();
  const serialModeRadioName = `safety_section_serial_mode_${radioInstanceId}`.replace(/:/g, "");

  // Check validation state
  const validation = useMemo(
    () => validateSectionConfig(normConfig, totalQuestions),
    [normConfig, totalQuestions]
  );

  // Hardened update helper reading latest prop source
  const updateConfig = (updater) => {
    const current = normalizeSectionConfig(sectionConfig);
    const next = typeof updater === "function" ? updater(current) : updater;
    const normalizedNext = normalizeSectionConfig(next);
    onChange?.(normalizedNext);
  };

  // Toggle enabled directly with boolean value
  const handleToggleEnabled = (enabled) => {
    updateConfig((prev) => ({
      ...prev,
      enabled: Boolean(enabled),
      // If turning on for the first time with 0 sections, create an initial section
      sections:
        enabled && prev.sections.length === 0 && totalQuestions > 0
          ? [
              {
                key: generateSectionKey(),
                label: "GENERAL",
                order_index: 1,
                question_order_indexes: Array.from(
                  { length: totalQuestions },
                  (_, i) => i + 1
                ),
              },
            ]
          : prev.sections,
    }));
  };

  // Change serial mode without resetting other fields
  const handleSerialModeChange = (mode) => {
    updateConfig((prev) => ({
      ...prev,
      serial_number_mode: mode,
    }));
  };

  // Add new section
  const handleAddSection = () => {
    // Find next unassigned start index
    const assigned = new Set();
    normConfig.sections.forEach((s) => {
      (s.question_order_indexes || []).forEach((idx) => assigned.add(idx));
    });

    let nextUnassigned = 1;
    while (assigned.has(nextUnassigned) && nextUnassigned <= totalQuestions) {
      nextUnassigned += 1;
    }

    const defaultQuestions =
      nextUnassigned <= totalQuestions ? [nextUnassigned] : [];

    const newSec = {
      key: generateSectionKey(),
      label: `SECTION ${normConfig.sections.length + 1}`,
      order_index: normConfig.sections.length + 1,
      question_order_indexes: defaultQuestions,
    };

    updateConfig((prev) => ({
      ...prev,
      sections: [...prev.sections, newSec],
    }));
  };

  // Update specific section
  const handleUpdateSection = (index, updates) => {
    updateConfig((prev) => {
      const nextSections = [...prev.sections];
      nextSections[index] = { ...nextSections[index], ...updates };
      return { ...prev, sections: nextSections };
    });
  };

  // Delete section (questions are preserved, just unsectioned)
  const handleDeleteSection = (index) => {
    updateConfig((prev) => {
      const filtered = prev.sections.filter((_, i) => i !== index);
      // Re-index order_index 1..N
      const reindexed = filtered.map((s, idx) => ({
        ...s,
        order_index: idx + 1,
      }));
      return { ...prev, sections: reindexed };
    });
  };

  // Move section Up/Down
  const handleMoveSection = (index, direction) => {
    const targetIdx = index + direction;
    if (targetIdx < 0 || targetIdx >= normConfig.sections.length) return;

    updateConfig((prev) => {
      const next = [...prev.sections];
      const temp = next[index];
      next[index] = next[targetIdx];
      next[targetIdx] = temp;
      return {
        ...prev,
        sections: next.map((s, i) => ({ ...s, order_index: i + 1 })),
      };
    });
  };

  // Quick range assignment for a section: from question startIdx to endIdx (1-based)
  const handleSetSectionRange = (sectionIndex, start, end) => {
    const s = Math.max(1, Math.min(Number(start) || 1, totalQuestions));
    const e = Math.max(s, Math.min(Number(end) || s, totalQuestions));

    const newIndexes = [];
    for (let i = s; i <= e; i += 1) {
      newIndexes.push(i);
    }

    updateConfig((prev) => {
      const newSections = prev.sections.map((sec, idx) => {
        if (idx === sectionIndex) {
          return { ...sec, question_order_indexes: newIndexes };
        }
        // Remove overlapping indexes from other sections to enforce single section membership
        const remaining = (sec.question_order_indexes || []).filter(
          (q) => q < s || q > e
        );
        return { ...sec, question_order_indexes: remaining };
      });
      return { ...prev, sections: newSections };
    });
  };

  return (
    <div
      className={`rounded-2xl border border-gray-200 bg-white p-5 shadow-sm ${className}`}
    >
      {/* Top Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-4">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-100 text-orange-600">
            <Layers className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">
              Partitions / Sections
            </h3>
            <p className="text-xs text-gray-500">
              Group questions into distinct visual bands (e.g. ACCESS & EGRESS, OFFICE, TOILETS)
            </p>
          </div>
        </div>

        {/* Master Controlled Switch */}
        <div className="flex items-center gap-2.5">
          <span className="text-xs font-semibold text-gray-700">
            {normConfig.enabled ? "Enabled" : "Disabled"}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={normConfig.enabled}
            onClick={() => handleToggleEnabled(!normConfig.enabled)}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${
              normConfig.enabled ? "bg-orange-500" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform ${
                normConfig.enabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      </div>

      {normConfig.enabled && (
        <div className="mt-4 space-y-5">
          {/* Serial Numbering Mode Selector */}
          <fieldset className="rounded-xl border border-orange-100 bg-orange-50/50 p-3.5">
            <div className="flex items-center gap-2 mb-2">
              <Hash className="h-4 w-4 text-orange-600" />
              <legend className="text-xs font-bold uppercase tracking-wider text-orange-950">
                Question Serial Numbering:
              </legend>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label
                className={`flex cursor-pointer items-start gap-2.5 rounded-lg border p-2.5 transition ${
                  normConfig.serial_number_mode === "GLOBAL"
                    ? "border-orange-400 bg-white shadow-xs"
                    : "border-transparent bg-transparent hover:bg-white/60 text-gray-600"
                }`}
              >
                <input
                  type="radio"
                  name={serialModeRadioName}
                  value="GLOBAL"
                  checked={normConfig.serial_number_mode === "GLOBAL"}
                  onChange={() => handleSerialModeChange("GLOBAL")}
                  className="mt-0.5 text-orange-500 focus:ring-orange-400"
                />
                <div>
                  <div className="text-xs font-bold text-gray-900">
                    Continuous (Global 1..N)
                  </div>
                  <div className="text-[11px] text-gray-500">
                    Questions continue sequentially (1, 2, 3, 4, 5...) across all sections.
                  </div>
                </div>
              </label>

              <label
                className={`flex cursor-pointer items-start gap-2.5 rounded-lg border p-2.5 transition ${
                  normConfig.serial_number_mode === "RESET_PER_SECTION"
                    ? "border-orange-400 bg-white shadow-xs"
                    : "border-transparent bg-transparent hover:bg-white/60 text-gray-600"
                }`}
              >
                <input
                  type="radio"
                  name={serialModeRadioName}
                  value="RESET_PER_SECTION"
                  checked={normConfig.serial_number_mode === "RESET_PER_SECTION"}
                  onChange={() => handleSerialModeChange("RESET_PER_SECTION")}
                  className="mt-0.5 text-orange-500 focus:ring-orange-400"
                />
                <div>
                  <div className="text-xs font-bold text-gray-900">
                    Restart in Each Section (1, 2, 3...)
                  </div>
                  <div className="text-[11px] text-gray-500">
                    Numbering resets back to 1 under every section title band.
                  </div>
                </div>
              </label>
            </div>
          </fieldset>

          {/* Validation Warning Alert if any */}
          {!validation.valid && (
            <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5 text-red-500" />
              <span>{validation.error}</span>
            </div>
          )}

          {/* Sections List */}
          <div className="space-y-3">
            {normConfig.sections.map((sec, sIdx) => {
              const qIndexes = sec.question_order_indexes || [];
              const startQ = qIndexes.length > 0 ? Math.min(...qIndexes) : 1;
              const endQ = qIndexes.length > 0 ? Math.max(...qIndexes) : 1;

              return (
                <div
                  key={sec.key || sIdx}
                  className="rounded-xl border border-gray-200 bg-gray-50/70 p-3.5 shadow-xs transition hover:border-gray-300"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                      <span className="flex h-6 w-6 items-center justify-center rounded-md bg-gray-200 text-xs font-bold text-gray-700">
                        {sIdx + 1}
                      </span>
                      <input
                        type="text"
                        value={sec.label}
                        onChange={(e) =>
                          handleUpdateSection(sIdx, { label: e.target.value })
                        }
                        placeholder="e.g. ACCESS & EGRESS"
                        className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-bold text-gray-900 shadow-xs focus:border-orange-500 focus:outline-none"
                      />
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        disabled={sIdx === 0}
                        onClick={() => handleMoveSection(sIdx, -1)}
                        className="rounded-lg border border-gray-200 bg-white p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                        title="Move Up"
                      >
                        <ChevronUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        disabled={sIdx === normConfig.sections.length - 1}
                        onClick={() => handleMoveSection(sIdx, 1)}
                        className="rounded-lg border border-gray-200 bg-white p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                        title="Move Down"
                      >
                        <ChevronDown className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteSection(sIdx)}
                        className="rounded-lg border border-red-200 bg-white p-1 text-red-500 hover:bg-red-50"
                        title="Delete Section (questions remain intact)"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Question Range Selection */}
                  <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200/80 pt-2.5 text-xs text-gray-600">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-700">
                        Questions:
                      </span>
                      <div className="flex items-center gap-1.5">
                        <span>From Q</span>
                        <input
                          type="number"
                          min={1}
                          max={totalQuestions}
                          value={startQ}
                          onChange={(e) =>
                            handleSetSectionRange(
                              sIdx,
                              e.target.value,
                              Math.max(Number(e.target.value), endQ)
                            )
                          }
                          className="w-14 rounded-md border border-gray-300 bg-white px-2 py-1 text-center text-xs font-bold text-gray-900 focus:border-orange-500 focus:outline-none"
                        />
                        <span>to Q</span>
                        <input
                          type="number"
                          min={startQ}
                          max={totalQuestions}
                          value={endQ}
                          onChange={(e) =>
                            handleSetSectionRange(
                              sIdx,
                              startQ,
                              Math.max(startQ, Number(e.target.value))
                            )
                          }
                          className="w-14 rounded-md border border-gray-300 bg-white px-2 py-1 text-center text-xs font-bold text-gray-900 focus:border-orange-500 focus:outline-none"
                        />
                      </div>
                    </div>

                    <div className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-[11px] font-bold text-green-800">
                      {qIndexes.length} Question
                      {qIndexes.length !== 1 ? "s" : ""} Assigned
                    </div>
                  </div>
                </div>
              );
            })}

            {normConfig.sections.length === 0 && (
              <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center text-xs text-gray-500">
                No sections defined yet. Click "+ Add Section" below to create your first section.
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              onClick={handleAddSection}
              className="inline-flex items-center gap-1.5 rounded-xl border border-orange-200 bg-orange-50 px-3.5 py-2 text-xs font-bold text-orange-600 transition hover:bg-orange-100"
            >
              <Plus className="h-4 w-4" />
              Add Section
            </button>

            <span className="text-[11px] text-gray-400">
              Total questions: {totalQuestions}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export default SafetySectionManager;
